import { Modal, Button, Row, Col, Alert } from 'react-bootstrap';
import { FaDownload, FaEye, FaFileInvoice, FaCopy } from 'react-icons/fa';
import { INfeResponse } from '../../../interfaces/INfe';
import { useState } from 'react';

interface INfeSuccessModalProps {
  show: boolean;
  onHide: () => void;
  nfeData: INfeResponse | null;
}

export default function NfeSuccessModal({ show, onHide, nfeData }: INfeSuccessModalProps) {
  const [copied, setCopied] = useState(false);

  if (!nfeData || !nfeData.body) return null;

  const { body } = nfeData;
  const hasError = !!body.error;
  const isApproved = !hasError && body.status === 'aprovado';
  const isRejected = hasError || body.status === 'rejeitado';

  const copyChaveToClipboard = () => {
    navigator.clipboard.writeText(body.chave);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (url: string, filename?: string) => {
    const link = document.createElement('a');
    link.href = url;
    if (filename) {
      link.download = filename;
    }
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className={isApproved ? 'bg-success text-white' : 'bg-danger text-white'}>
        <Modal.Title>
          {hasError ? 'Erro na Emissão da NFe' : isApproved ? 'NFe Emitida com Sucesso!' : 'Erro na Emissão da NFe'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {hasError ? (
          <Alert variant="danger">
            <Alert.Heading>Erro na Validação dos Dados</Alert.Heading>
            <p className="mb-0">{body.error}</p>
          </Alert>
        ) : isApproved ? (
          <Alert variant="success">
            <strong>Parabéns!</strong> Sua nota fiscal foi emitida e autorizada com sucesso.
          </Alert>
        ) : (
          <Alert variant="danger">
            <strong>Atenção!</strong> Houve um problema na emissão da nota fiscal.
          </Alert>
        )}

        {!hasError && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <strong>Número da NFe:</strong> {body.nfe || '---'}
              </Col>
              <Col md={6}>
                <strong>Série:</strong> {body.serie || '---'}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <strong>Status:</strong> 
                <span className={`badge ms-2 ${isApproved ? 'bg-success' : 'bg-danger'}`}>
                  {body.status || 'Desconhecido'}
                </span>
              </Col>
              <Col md={6}>
                <strong>Modelo:</strong> {body.modelo ? body.modelo.toString().toUpperCase() : 'NFE'}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <strong>Motivo:</strong> {body.motivo || 'Não informado'}
              </Col>
            </Row>

            {body.chave && (
              <Row className="mb-3">
                <Col>
                  <strong>Chave de Acesso:</strong>
                  <div className="d-flex align-items-center mt-2">
                    <code className="bg-light p-2 rounded flex-grow-1 me-2" style={{ fontSize: '12px' }}>
                      {body.chave}
                    </code>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={copyChaveToClipboard}
                      title="Copiar chave"
                    >
                      <FaCopy size={14} />
                    </Button>
                  </div>
                  {copied && <small className="text-success">Chave copiada!</small>}
                </Col>
              </Row>
            )}
          </>
        )}

        {isApproved && (body.danfe || body.xml) && (
          <>
            <hr />
            <h5>Downloads Disponíveis</h5>
            <Row className="g-3">
              {body.danfe && (
                <Col md={6}>
                  <div className="d-grid">
                    <Button 
                      variant="outline-primary"
                      onClick={() => handleDownload(body.danfe, `NFe_${body.nfe}_${body.serie}.pdf`)}
                    >
                      <FaDownload className="me-2" />
                      DANFE (PDF)
                    </Button>
                  </div>
                </Col>
              )}

              {body.xml && (
                <Col md={6}>
                  <div className="d-grid">
                    <Button 
                      variant="outline-success"
                      onClick={() => handleDownload(body.xml, `NFe_${body.chave}.xml`)}
                    >
                      <FaFileInvoice className="me-2" />
                      XML da NFe
                    </Button>
                  </div>
                </Col>
              )}

              {body.danfe_simples && (
                <Col md={6}>
                  <div className="d-grid">
                    <Button 
                      variant="outline-info"
                      onClick={() => handleDownload(body.danfe_simples, `NFe_Simples_${body.nfe}_${body.serie}.pdf`)}
                    >
                      <FaEye className="me-2" />
                      DANFE Simples
                    </Button>
                  </div>
                </Col>
              )}

              {body.danfe_etiqueta && (
                <Col md={6}>
                  <div className="d-grid">
                    <Button 
                      variant="outline-warning"
                      onClick={() => handleDownload(body.danfe_etiqueta, `NFe_Etiqueta_${body.nfe}_${body.serie}.pdf`)}
                    >
                      <FaDownload className="me-2" />
                      DANFE Etiqueta
                    </Button>
                  </div>
                </Col>
              )}
            </Row>
          </>
        )}

        {!hasError && body.log && body.log.aProt && body.log.aProt.length > 0 && (
          <>
            <hr />
            <h6>Detalhes Técnicos</h6>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Protocolo:</strong> {body.log.aProt[0].nProt}
              </Col>
              <Col md={6}>
                <strong>Data de Recebimento:</strong> {new Date(body.log.aProt[0].dhRecbto).toLocaleString('pt-BR')}
              </Col>
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}